import React from 'react';
import { Phone, Mail, MapPin, Shield, Star } from 'lucide-react';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

export const Footer = ({ theme, language, t, setLegalPage, darkMode = true }) => {
  const paymentMethods = [
    { name: 'Visa', color: '#1A1F71', bg: '#FFFFFF' },
    { name: 'Mastercard', color: '#EB001B', bg: '#F79E1B' },
    { name: 'PayPal', color: '#003087', bg: '#009CDE' },
    { name: 'Apple Pay', color: '#000000', bg: '#FFFFFF' },
    { name: 'Google Pay', color: '#4285F4', bg: '#FFFFFF' },
    { name: 'Klarna', color: '#FFB3C7', bg: '#FFB3C7' },
    { name: 'SEPA', color: BRAND.turquoise, bg: BRAND.turquoise },
  ];

  return (
    <footer 
      data-section="contact" 
      className="py-16" 
      style={{ 
        backgroundColor: darkMode ? '#050505' : '#f8fafc', 
        borderTop: `1px solid ${theme.border}` 
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand & Description */}
          <div className="lg:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_37cca134-41a5-49ca-9252-6c0a7dd12fc1/artifacts/rsel26pf_Fancy_Donuts_Wortmarke.png" 
              alt="Fancy Donuts" 
              className="h-10 w-auto mb-4"
            />
            <p className="text-sm mb-6" style={{ color: theme.textMuted }}>
              {language === 'de' 
                ? 'Premium Donuts für Unternehmen. Handgemacht in Potsdam, geliefert deutschlandweit.'
                : 'Premium donuts for businesses. Handmade in Potsdam, delivered throughout Germany.'}
            </p>
            {/* Social Icons */}
            <div className="flex gap-3">
              {['Instagram', 'Facebook', 'LinkedIn'].map(social => (
                <a 
                  key={social} 
                  href="#" 
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:scale-110"
                  style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}
                >
                  <span className="text-xs font-bold" style={{ color: theme.textMuted }}>{social[0]}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: BRAND.turquoise }}>
              {t.footer.company}
            </h4>
            <ul className="space-y-3">
              {[t.footer.about, t.footer.careers, t.footer.press].map(item => (
                <li key={item}>
                  <a href="#" className="text-sm transition-colors hover:opacity-70" style={{ color: theme.textMuted }}>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: BRAND.pink }}>
              {t.footer.legal}
            </h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => { setLegalPage('agb'); window.scrollTo(0, 0); }} 
                  className="text-sm transition-colors hover:opacity-70 text-left" 
                  style={{ color: theme.textMuted }}
                >
                  {t.footer.agb}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => { setLegalPage('datenschutz'); window.scrollTo(0, 0); }} 
                  className="text-sm transition-colors hover:opacity-70 text-left" 
                  style={{ color: theme.textMuted }}
                >
                  {t.footer.privacy}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => { setLegalPage('impressum'); window.scrollTo(0, 0); }} 
                  className="text-sm transition-colors hover:opacity-70 text-left" 
                  style={{ color: theme.textMuted }}
                >
                  {t.footer.imprint}
                </button>
              </li>
              <li>
                <button 
                  onClick={() => { setLegalPage('widerruf'); window.scrollTo(0, 0); }} 
                  className="text-sm transition-colors hover:opacity-70 text-left" 
                  style={{ color: theme.textMuted }}
                >
                  {language === 'de' ? 'Widerrufsbelehrung' : 'Cancellation Policy'}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-sm uppercase tracking-wider mb-4" style={{ color: BRAND.yellow }}>
              {t.footer.contact}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-sm" style={{ color: theme.textMuted }}>
                <Phone className="w-4 h-4" style={{ color: BRAND.turquoise }} />
                <span>033 123 54 77 54</span>
              </li>
              <li className="flex items-center gap-2 text-sm" style={{ color: theme.textMuted }}>
                <Mail className="w-4 h-4" style={{ color: BRAND.pink }} />
                <span>info@fancy-donuts.com</span>
              </li>
              <li className="flex items-start gap-2 text-sm" style={{ color: theme.textMuted }}>
                <MapPin className="w-4 h-4 mt-0.5" style={{ color: BRAND.yellow }} />
                <span>Charlottenstraße 116<br />14467 Potsdam</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-b py-8 mb-8" style={{ borderColor: theme.border }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="font-bold text-sm mb-3" style={{ color: theme.textMuted }}>
                {t.footer.payment}
              </h4>
              <div className="flex flex-wrap gap-2">
                {paymentMethods.map(method => (
                  <div 
                    key={method.name} 
                    className="px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5"
                    style={{ 
                      background: `linear-gradient(135deg, ${method.color}20, ${method.bg}15)`,
                      color: method.color,
                      border: `1px solid ${method.color}40`
                    }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: method.bg }}></span>
                    {method.name}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-1" 
                  style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#000000' }}
                >
                  <Shield className="w-6 h-6" style={{ color: darkMode ? BRAND.turquoise : '#ffffff' }} />
                </div>
                <span className="text-xs" style={{ color: theme.textMuted }}>SSL</span>
              </div>
              <div className="text-center">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-1" 
                  style={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#000000' }}
                >
                  <Star className="w-6 h-6" style={{ color: darkMode ? BRAND.pink : '#ffffff' }} />
                </div>
                <span className="text-xs" style={{ color: theme.textMuted }}>Trusted</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <p className="text-sm" style={{ color: theme.textMuted }}>
            {t.footer.copyright}
          </p>
          <p className="text-sm" style={{ color: theme.textMuted }}>
            {t.footer.madeWith}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
